import {
  insideAny,
  letterTokens,
  quotedSpans,
  type Span,
  type Token,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * Anthropomorphism: a non-human subject doing something only a person can do.
 *
 * APA 7 (Publication Manual, section 4.11) asks writers not to credit animals or
 * inanimate sources with human actions — "the study concluded", "the theory believes",
 * "the paper argues" — and to pair an active verb with a human actor ("the researchers
 * concluded") or choose a verb that is literally true of the source ("the data
 * indicate"). The manual also accepts widespread constructions that "do not impede
 * understanding or mislead readers", and "the results suggest" or "the table shows" are
 * the standard examples.
 *
 * This module finds the pattern *subject noun + (a little glue) + verb* where the noun
 * names a study, text, theory, display, body of evidence or organisation and the verb
 * comes from a hand-built list sorted into three tiers:
 *
 *   likely    — thinking, feeling, wanting, trying: only a person does these
 *   check     — speech-act verbs (argue, conclude, recommend) that many fields avoid
 *               for non-human subjects but use freely for texts
 *   accepted  — the widely used constructions APA leaves alone (show, indicate, find…)
 *
 * It is a pattern matcher, not a parser: it cannot know that "the group" means people
 * or that "the study" is a nickname for a lab. It skips quoted passages, and anything
 * it reports is a prompt to decide, never a mistake.
 */

export type SubjectKind =
  | "research"
  | "text"
  | "theory"
  | "display"
  | "evidence"
  | "organization";

export type Tier = "likely" | "check" | "accepted";

interface SubjectEntry {
  kind: SubjectKind;
  plural: boolean;
}

function subjects(
  kind: SubjectKind,
  singular: readonly string[],
  plural: readonly string[]
): [string, SubjectEntry][] {
  return [
    ...singular.map((word): [string, SubjectEntry] => [
      word,
      { kind, plural: false },
    ]),
    ...plural.map((word): [string, SubjectEntry] => [
      word,
      { kind, plural: true },
    ]),
  ];
}

const SUBJECTS: ReadonlyMap<string, SubjectEntry> = new Map([
  ...subjects(
    "research",
    [
      "study",
      "experiment",
      "survey",
      "questionnaire",
      "analysis",
      "research",
      "project",
      "trial",
      "investigation",
      "inquiry",
    ],
    [
      "studies",
      "experiments",
      "surveys",
      "questionnaires",
      "analyses",
      "projects",
      "trials",
      "investigations",
    ]
  ),
  ...subjects(
    "text",
    [
      "paper",
      "article",
      "chapter",
      "essay",
      "report",
      "review",
      "thesis",
      "dissertation",
      "book",
      "text",
      "section",
      "manuscript",
      "abstract",
      "document",
    ],
    ["papers", "articles", "chapters", "essays", "reports", "reviews", "books"]
  ),
  ...subjects("text", ["literature"], []),
  ...subjects(
    "theory",
    ["theory", "hypothesis", "model", "framework", "approach", "perspective"],
    ["theories", "hypotheses", "models", "frameworks", "approaches"]
  ),
  ...subjects(
    "display",
    ["table", "figure", "graph", "chart", "diagram", "appendix"],
    ["tables", "figures", "graphs", "charts", "diagrams"]
  ),
  ...subjects(
    "evidence",
    ["data", "evidence", "finding", "result", "statistic"],
    ["findings", "results", "statistics"]
  ),
  ...subjects(
    "organization",
    [
      "company",
      "corporation",
      "organization",
      "organisation",
      "hospital",
      "school",
      "university",
      "government",
      "agency",
    ],
    []
  ),
]);

/** Subjects whose "assumes", "predicts" and "requires" are plain technical usage. */
const TECHNICAL_KINDS: ReadonlySet<SubjectKind> = new Set(["theory"]);
const TECHNICAL_SUBJECT_WORDS: ReadonlySet<string> = new Set([
  "analysis",
  "analyses",
  "test",
  "tests",
]);

interface VerbForms {
  base: string;
  forms: string[];
  /** The word that must follow ("look" + "at"). */
  particle?: string;
  tier: Tier;
  /** Tier when the subject is a model, theory or statistical analysis. */
  technical?: Tier;
  /** Literal verbs that are true of a text or a display. */
  literal: string[];
}

const RE_ENDS_E = /e$/;
const RE_DROP_E = /(?<![eoy])e$/;
const RE_HAS = /\bhas\b/g;
const RE_HAVE = /\bhave\b/g;
const RE_DOES = /\bdoes\b/g;
const RE_DO = /\bdo\b/g;
const RE_ENDS_Y = /[^aeiou]y$/;
const RE_ENDS_SIBILANT = /(?:s|x|z|ch|sh)$/;

function thirdPerson(base: string): string {
  if (RE_ENDS_Y.test(base)) {
    return `${base.slice(0, -1)}ies`;
  }
  return RE_ENDS_SIBILANT.test(base) ? `${base}es` : `${base}s`;
}

function past(base: string): string {
  if (RE_ENDS_E.test(base)) {
    return `${base}d`;
  }
  return RE_ENDS_Y.test(base) ? `${base.slice(0, -1)}ied` : `${base}ed`;
}

function gerund(base: string): string {
  return RE_DROP_E.test(base) ? `${base.slice(0, -1)}ing` : `${base}ing`;
}

interface VerbSpec {
  tier: Tier;
  technical?: Tier;
  literal?: string[];
  /** Irregular forms beyond base, 3rd person and -ing; regular past is added unless `past` is given. */
  extra?: string[];
  particle?: string;
  /** Skip the generated regular past form (irregular verbs list their own). */
  irregular?: boolean;
}

function verbs(spec: VerbSpec, ...bases: readonly string[]): VerbForms[] {
  return bases.map((base) => ({
    base,
    forms: [
      base,
      thirdPerson(base),
      gerund(base),
      ...(spec.irregular ? [] : [past(base)]),
      ...(spec.extra ?? []),
    ],
    particle: spec.particle,
    tier: spec.tier,
    technical: spec.technical,
    literal: spec.literal ?? [],
  }));
}

/** Verb entries keyed by every form they can take. */
const VERB_LIST: VerbForms[] = [
  // Thinking, feeling, wanting, trying: only a person does these.
  ...verbs(
    { tier: "likely" },
    "believe",
    "want",
    "hope",
    "wish",
    "intend",
    "decide",
    "wonder",
    "ask",
    "agree",
    "disagree",
    "refuse",
    "attempt",
    "struggle",
    "remember",
    "learn",
    "notice",
    "realize",
    "realise",
    "worry",
    "fear",
    "enjoy",
    "love",
    "hate",
    "care",
    "try",
    "deny"
  ),
  ...verbs({ tier: "likely", irregular: true, extra: ["thought"] }, "think"),
  ...verbs({ tier: "likely", irregular: true, extra: ["felt"] }, "feel"),
  ...verbs(
    { tier: "likely", irregular: true, extra: ["forgot", "forgotten"] },
    "forget"
  ),
  ...verbs(
    { tier: "likely", irregular: true, extra: ["preferred", "preferring"] },
    "prefer"
  ),
  ...verbs(
    { tier: "likely", irregular: true, extra: ["admitted", "admitting"] },
    "admit"
  ),
  ...verbs(
    { tier: "likely", irregular: true, extra: ["knew", "known"] },
    "know"
  ),
  ...verbs(
    { tier: "likely", irregular: true, extra: ["understood"] },
    "understand"
  ),
  ...verbs(
    { tier: "likely", irregular: true, extra: ["chose", "chosen"] },
    "choose"
  ),
  ...verbs({ tier: "likely", irregular: true, extra: ["sought"] }, "seek"),
  ...verbs(
    { tier: "likely", irregular: true, extra: ["strove", "striven"] },
    "strive"
  ),
  // Speech acts and stances: many fields reserve them for people.
  ...verbs(
    { tier: "check", literal: ["indicates", "supports the conclusion that"] },
    "conclude"
  ),
  ...verbs(
    { tier: "check", literal: ["proposes", "presents the argument that"] },
    "argue"
  ),
  ...verbs({ tier: "check", literal: ["states"] }, "claim", "assert", "insist"),
  ...verbs({ tier: "check", literal: ["states", "reports"] }, "state"),
  ...verbs(
    { tier: "check", irregular: true, extra: ["said"], literal: ["states"] },
    "say"
  ),
  ...verbs(
    { tier: "check", irregular: true, extra: ["told"], literal: ["shows"] },
    "tell"
  ),
  ...verbs(
    { tier: "check", literal: ["contains the recommendation that", "lists"] },
    "recommend",
    "advocate"
  ),
  ...verbs(
    { tier: "check", literal: ["notes"] },
    "emphasize",
    "emphasise",
    "stress",
    "warn",
    "urge",
    "acknowledge",
    "recognize",
    "recognise",
    "promise",
    "challenge",
    "answer"
  ),
  ...verbs(
    {
      tier: "check",
      irregular: true,
      extra: ["saw", "seen"],
      literal: ["treats"],
    },
    "see"
  ),
  ...verbs(
    {
      tier: "check",
      technical: "accepted",
      literal: ["is based on the assumption that"],
    },
    "assume",
    "expect"
  ),
  ...verbs(
    {
      tier: "check",
      irregular: true,
      extra: ["spoke", "spoken"],
      literal: ["addresses"],
      particle: "to",
    },
    "speak"
  ),
  ...verbs(
    { tier: "check", literal: ["examines", "addresses"], particle: "at" },
    "look"
  ),
  ...verbs(
    {
      tier: "check",
      irregular: true,
      extra: ["setting"],
      literal: ["is designed to"],
      particle: "out",
    },
    "set"
  ),
  ...verbs({ tier: "check", literal: ["notes"], particle: "out" }, "point"),
  // Widely used constructions that APA leaves alone.
  ...verbs(
    { tier: "accepted" },
    "indicate",
    "suggest",
    "demonstrate",
    "reveal",
    "report",
    "present",
    "describe",
    "discuss",
    "address",
    "focus",
    "provide",
    "include",
    "contain",
    "support",
    "illustrate",
    "display",
    "list",
    "aim",
    "examine",
    "explore",
    "investigate",
    "evaluate",
    "explain",
    "outline",
    "summarize",
    "summarise",
    "compare",
    "analyze",
    "analyse",
    "propose",
    "imply",
    "confirm",
    "document",
    "highlight",
    "identify",
    "define",
    "offer",
    "predict",
    "require",
    "allow",
    "need"
  ),
  ...verbs({ tier: "accepted", irregular: true, extra: ["found"] }, "find"),
  ...verbs({ tier: "accepted", extra: ["shown"] }, "show"),
];

const VERB_BY_FORM: ReadonlyMap<string, VerbForms> = (() => {
  const map = new Map<string, VerbForms>();
  for (const entry of VERB_LIST) {
    for (const form of entry.forms) {
      if (!map.has(form)) {
        map.set(form, entry);
      }
    }
  }
  return map;
})();

/** Forms that are also ordinary words and need a particle to count ("set", "look"). */
const PARTICLE_ONLY = new Set([
  "set",
  "sets",
  "look",
  "looks",
  "point",
  "points",
  "speak",
]);

const INTERVENING: ReadonlySet<string> = new Set([
  "also",
  "then",
  "not",
  "never",
  "always",
  "clearly",
  "explicitly",
  "repeatedly",
  "actually",
  "really",
  "only",
  "simply",
  "even",
  "ultimately",
  "further",
  "largely",
  "still",
  "just",
  "already",
  "later",
  "first",
  "has",
  "have",
  "had",
  "does",
  "do",
  "did",
  "will",
  "would",
  "can",
  "could",
  "may",
  "might",
  "should",
  "must",
  "that",
  "which",
]);
const MAX_INTERVENING = 2;

/** A word before the subject that shows it is not in subject position. */
const BLOCKERS: ReadonlySet<string> = new Set([
  "of",
  "in",
  "on",
  "at",
  "by",
  "for",
  "with",
  "from",
  "to",
  "into",
  "onto",
  "about",
  "as",
  "through",
  "over",
  "under",
  "between",
  "among",
  "during",
  "across",
  "against",
  "within",
  "without",
  "toward",
  "towards",
  "upon",
  "via",
  "per",
  "than",
  "who",
  "whom",
  "whose",
  "where",
  "what",
  "how",
  "why",
  "is",
  "are",
  "was",
  "were",
  "be",
  "been",
  "being",
  "conducted",
  "ran",
  "completed",
  "performed",
  "designed",
  "read",
  "wrote",
  "used",
  "reviewed",
  "cited",
  "followed",
  "replicated",
  "analysed",
  "analyzed",
  "examined",
]);

/** Words that open a new clause: the subject noun that follows has nothing before it. */
const CLAUSE_OPENERS: ReadonlySet<string> = new Set([
  "and",
  "but",
  "or",
  "so",
  "yet",
  "nor",
  "because",
  "although",
  "though",
  "while",
  "whereas",
  "unless",
  "until",
  "once",
  "if",
  "when",
  "since",
  "whether",
  "that",
  "after",
  "before",
  "thus",
  "hence",
  "however",
  "therefore",
  "moreover",
  "furthermore",
  "then",
  "which",
]);

const DETERMINERS: ReadonlySet<string> = new Set([
  "the",
  "this",
  "that",
  "these",
  "those",
  "our",
  "my",
  "their",
  "its",
  "his",
  "her",
  "a",
  "an",
  "each",
  "every",
  "any",
  "such",
]);

const MODIFIERS: ReadonlySet<string> = new Set([
  "present",
  "current",
  "previous",
  "prior",
  "earlier",
  "recent",
  "original",
  "same",
  "whole",
  "entire",
  "final",
  "first",
  "second",
  "pilot",
  "longitudinal",
  "qualitative",
  "quantitative",
  "empirical",
  "experimental",
  "published",
  "cited",
  "single",
  "large",
  "small",
  "many",
  "several",
  "other",
  "existing",
  "relevant",
  "available",
  "general",
  "central",
]);

const RE_CLAUSE_BREAK = /[,;:()–—.!?"“”]/;

export interface AnthropomorphismFinding extends Span {
  /** The matched noun, e.g. "study". */
  subject: string;
  kind: SubjectKind;
  /** The verb as written, with its particle ("looks at"). */
  verb: string;
  verbBase: string;
  tier: Tier;
  /** The subject phrase, verb and anything between, as written. */
  text: string;
  /** A rewrite that names a person, or null where none fits (a table, an organisation). */
  suggestion: string | null;
  /** Verbs that are literally true of a text or a display. */
  literal: string[];
}

function norm(token: Token): string {
  return token.lower.normalize("NFC");
}

function clauseStartIndex(
  text: string,
  tokens: readonly Token[],
  noun: number
): number {
  let index = noun;
  while (index > 0) {
    const previous = tokens[index - 1];
    if (RE_CLAUSE_BREAK.test(text.slice(previous.end, tokens[index].start))) {
      return index;
    }
    if (CLAUSE_OPENERS.has(norm(previous))) {
      return index;
    }
    index -= 1;
  }
  return 0;
}

function isSubjectPosition(
  text: string,
  tokens: readonly Token[],
  noun: number
): boolean {
  const start = clauseStartIndex(text, tokens, noun);
  for (let index = start; index < noun; index += 1) {
    if (BLOCKERS.has(norm(tokens[index]))) {
      return false;
    }
  }
  return true;
}

interface VerbMatch {
  entry: VerbForms;
  tokenIndex: number;
  /** Index of the last token of the verb (the particle, if any). */
  lastIndex: number;
  form: string;
}

/** The verb that starts at `index`, if the word there is one and its particle follows. */
function verbAt(tokens: readonly Token[], index: number): VerbMatch | null {
  const word = norm(tokens[index]);
  const entry = VERB_BY_FORM.get(word);
  if (!entry) {
    return null;
  }
  if (entry.particle) {
    const next = tokens[index + 1];
    if (next === undefined || norm(next) !== entry.particle) {
      return null;
    }
    return { entry, tokenIndex: index, lastIndex: index + 1, form: word };
  }
  if (PARTICLE_ONLY.has(word)) {
    return null;
  }
  return { entry, tokenIndex: index, lastIndex: index, form: word };
}

/**
 * The verb after the subject noun, allowing up to two auxiliaries or adverbs between:
 * "the study has also concluded". Any other word in between means no match.
 */
function findVerb(tokens: readonly Token[], after: number): VerbMatch | null {
  let skipped = 0;
  for (let index = after + 1; index < tokens.length; index += 1) {
    if (VERB_BY_FORM.has(norm(tokens[index]))) {
      return verbAt(tokens, index);
    }
    if (!INTERVENING.has(norm(tokens[index])) || skipped >= MAX_INTERVENING) {
      return null;
    }
    skipped += 1;
  }
  return null;
}

function tierFor(entry: VerbForms, noun: string, subject: SubjectEntry): Tier {
  const technicalSubject =
    TECHNICAL_KINDS.has(subject.kind) || TECHNICAL_SUBJECT_WORDS.has(noun);
  let tier = technicalSubject && entry.technical ? entry.technical : entry.tier;
  if (subject.kind === "organization" && tier === "likely") {
    tier = "check";
  }
  return tier;
}

/* ------------------------------------------------------------ rewrite */

const RE_UPPER_START = /^\p{Lu}/u;

function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function actorFor(
  subject: SubjectEntry,
  determiner: string | null,
  phrase: string
): { actor: string; plural: boolean } | null {
  if (determiner === "our") {
    return subject.kind === "research" || subject.kind === "text"
      ? { actor: "we", plural: true }
      : null;
  }
  if (determiner === "my") {
    return subject.kind === "research" || subject.kind === "text"
      ? { actor: "I", plural: true }
      : null;
  }
  switch (subject.kind) {
    case "research":
    case "evidence":
      return { actor: "the researchers", plural: true };
    case "text":
      return subject.plural
        ? { actor: "the authors", plural: true }
        : { actor: "the author", plural: false };
    case "theory":
      return { actor: `proponents of ${phrase}`, plural: true };
    default:
      return null;
  }
}

function conjugate(
  entry: VerbForms,
  written: string,
  plural: boolean,
  hasAuxiliary: boolean
): string {
  if (hasAuxiliary) {
    return written;
  }
  const lower = written.toLowerCase().normalize("NFC");
  if (plural && lower === thirdPerson(entry.base)) {
    return entry.base;
  }
  if (!plural && lower === entry.base) {
    return thirdPerson(entry.base);
  }
  return written;
}

/** "has not" ↔ "have not" so the auxiliary agrees with the new actor. */
function agreeAuxiliary(middle: string, plural: boolean): string {
  return plural
    ? middle.replace(RE_HAS, "have").replace(RE_DOES, "do")
    : middle.replace(RE_HAVE, "has").replace(RE_DO, "does");
}

interface RewriteInput {
  text: string;
  tokens: readonly Token[];
  nounIndex: number;
  subject: SubjectEntry;
  match: VerbMatch;
}

function subjectPhraseStart(
  tokens: readonly Token[],
  nounIndex: number
): { startIndex: number; determiner: string | null } {
  let startIndex = nounIndex;
  let determiner: string | null = null;
  while (startIndex > 0) {
    const previous = norm(tokens[startIndex - 1]);
    if (DETERMINERS.has(previous)) {
      determiner = previous;
      startIndex -= 1;
      break;
    }
    if (MODIFIERS.has(previous)) {
      startIndex -= 1;
      continue;
    }
    break;
  }
  return { startIndex, determiner };
}

function buildSuggestion(input: RewriteInput): string | null {
  const { text, tokens, nounIndex, subject, match } = input;
  const { startIndex, determiner } = subjectPhraseStart(tokens, nounIndex);
  const noun = tokens[nounIndex];
  const phraseLower = text
    .slice(tokens[startIndex].start, noun.end)
    .toLowerCase();
  const actor = actorFor(subject, determiner, phraseLower);
  if (!actor) {
    return null;
  }
  const rawMiddle = text.slice(noun.end, tokens[match.tokenIndex].start);
  const middle = agreeAuxiliary(rawMiddle, actor.plural);
  const verbToken = tokens[match.tokenIndex];
  const verbText = conjugate(
    match.entry,
    text.slice(verbToken.start, verbToken.end),
    actor.plural,
    rawMiddle.trim().length > 0
  );
  const particle = match.entry.particle
    ? text.slice(verbToken.end, tokens[match.lastIndex].end)
    : "";
  const sentenceInitial = RE_UPPER_START.test(
    text.slice(tokens[startIndex].start, tokens[startIndex].start + 1)
  );
  const lead = sentenceInitial ? capitalise(actor.actor) : actor.actor;
  return `${lead}${middle}${verbText}${particle}`;
}

/* ------------------------------------------------------------ the scan */

export interface AnthropomorphismReport {
  findings: AnthropomorphismFinding[];
  /** Counts by tier, accepted constructions included. */
  counts: Record<Tier, number>;
  words: number;
}

export function findAnthropomorphism(text: string): AnthropomorphismReport {
  const tokens = letterTokens(text);
  const quoted = quotedSpans(text);
  const findings: AnthropomorphismFinding[] = [];
  for (const [index, token] of tokens.entries()) {
    const noun = norm(token);
    const subject = SUBJECTS.get(noun);
    if (!subject) {
      continue;
    }
    const match = findVerb(tokens, index);
    if (!(match && isSubjectPosition(text, tokens, index))) {
      continue;
    }
    const last = tokens[match.lastIndex];
    const { startIndex } = subjectPhraseStart(tokens, index);
    const start = tokens[startIndex].start;
    if (insideAny(quoted, start, last.end)) {
      continue;
    }
    const tier = tierFor(match.entry, noun, subject);
    findings.push({
      start,
      end: last.end,
      subject: token.text,
      kind: subject.kind,
      verb: text.slice(tokens[match.tokenIndex].start, last.end),
      verbBase: match.entry.base,
      tier,
      text: text.slice(start, last.end),
      suggestion:
        tier === "accepted"
          ? null
          : buildSuggestion({ text, tokens, nounIndex: index, subject, match }),
      literal: match.entry.literal,
    });
  }
  const counts: Record<Tier, number> = { likely: 0, check: 0, accepted: 0 };
  for (const finding of findings) {
    counts[finding.tier] += 1;
  }
  return { findings, counts, words: tokens.length };
}
